import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Input, Label, Row } from "reactstrap";
import { USER_COMPANIES } from "../../../../constants/modal.constant";
import Avatar from "../../Avatar";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { usePlanCore } from "../../../../cores/plan";
import { useResellerCore } from "../../../../cores/reseller";
import { User } from "../../../../cores/session/interfaces/models/user";
import { useUserCore } from "../../../../cores/user";
import { useIntl } from "react-intl";
import { getAllUserStatus } from "../../../../cores/user/constants/user-status";
import { PlanBadge } from "../../companies/PlanBadge";
import { StatusBadge } from "../../companies/StatusBadge";
import { InputWembii } from "../../InputWembii";
import { FreeTrialSelector } from "./FreeTrialSelector";
import { ResellerSelector } from "./ResellerSelector";
import UserCompanies from "./UserCompanies";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import { getFirstLetters } from "../../../utils/stringUtils";
import { useRouterCore } from "../../../../cores/router";
import { ADMIN_USER_AVATAR_LOADING } from "../../../../constants/loading.constant";
import { UpdateLoadingCard } from "../../UpdateLoadingCard";
import { beforeUploadFile, UploadFileType } from "../../../../cores/utils";
import { ME_CONFIG_RULES } from "../../../../cores/me/config";

interface Props {
  user: User;
  onAvatarUpdated?: () => void;
}

export default function EditUserForm(props: Props) {
  const intl = useIntl();
  const {
    user: {
      firstName,
      lastName,
      email,
      phoneNumber,
      phoneType,
      country,
      userRole,
      userPlan,
      id,
      avatarUrl: initialAvatarUrl,
      status,
      reseller,
      freeTrial,
      freeTrialDays,
      freeTrialDate,
    },
    onAvatarUpdated,
  } = props;
  const { goToPlans } = useRouterCore();
  const { plans, assignPlan, assignFreeTrial, unassignFreeTrial } =
    usePlanCore();
  const { putUser, postUserAvatar } = useUserCore();
  const { isAdmin, isReseller } = useMeCore();
  const { isLoading } = useGlobalCore();
  const [editPlan, setEditPlan] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [planId, setPlanId] = useState("");
  const [statusId, setStatusId] = useState(status);
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const { turnOnModal } = useGlobalCore();
  const { unassignResellerToUser, assignResellerToUser } = useResellerCore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filesAllowed = useMemo(
    () => [UploadFileType.JPEG, UploadFileType.JPG, UploadFileType.PNG],
    []
  );

  useEffect(() => {
    setAvatarUrl(initialAvatarUrl);
  }, [initialAvatarUrl, id]);

  useEffect(() => {
    userPlan && setPlanId("" + userPlan.id);
  }, [userPlan]);

  const oldPlan = useMemo(
    () => !plans.find(({ id: planOptionId }) => +planOptionId === +planId),
    [planId, plans]
  );

  const onAssignPlan = useCallback(
    async (_planId: string) => {
      await assignPlan(id + "", _planId);
      setPlanId(_planId);
      setEditPlan(false);
    },
    [assignPlan, id]
  );

  const onAssignStatus = useCallback(
    async (nextStatus: number) => {
      await putUser(id, { status: nextStatus });
      setStatusId(+nextStatus);
      setEditStatus(false);
    },
    [setEditStatus, putUser, id]
  );

  const onAssignReseller = useCallback(
    async (resellerId: number) => {
      if (resellerId) assignResellerToUser(resellerId, +id);
      else unassignResellerToUser(+id);
    },
    [assignResellerToUser, id, unassignResellerToUser]
  );

  const onAssignFreeTrial = useCallback(
    async (value: boolean, days: number) => {
      if (value) assignFreeTrial(id, days);
      else unassignFreeTrial(id);
    },
    [assignFreeTrial, id, unassignFreeTrial]
  );

  const onClickCompanies = useCallback(() => {
    turnOnModal(USER_COMPANIES, <UserCompanies id={id} />, Position.LEFT);
  }, [id, turnOnModal]);

  const handleSelectPhoto = useCallback(
    (files: FileList | null) => {
      if (!files?.length || !(isAdmin || isReseller)) return;

      const isValid = beforeUploadFile(
        files[0],
        ME_CONFIG_RULES.maxImageSize,
        filesAllowed
      );

      if (!isValid) return;

      postUserAvatar(`${id}`, files[0], (nextAvatarUrl) => {
        setAvatarUrl(nextAvatarUrl);
        onAvatarUpdated?.();
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [filesAllowed, id, isAdmin, isReseller, onAvatarUpdated, postUserAvatar]
  );

  const plan = useMemo(
    () => {
      const matchedPlan = plans.find(({ id: planOptionId }) => +planOptionId === +planId);

      return matchedPlan ? matchedPlan : userPlan
    },
    [planId, userPlan, plans]
  );


  return (
    <Container className="h-100" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div className="d-flex flex-column h-100 justify-content-center p-4">
            <h2 className="h2 text-center mb-4">{`Usuario #${id}`}</h2>
            <div className="mb-2 d-flex justify-content-center flex-column align-items-center ">
              <div className="position-relative">
                {!isLoading(ADMIN_USER_AVATAR_LOADING) && (
                  <Avatar
                    className="text-dark-50 mx-2"
                    label={getFirstLetters(`${lastName} ${firstName}`)}
                    width={"150px"}
                    image={avatarUrl}
                    height={"150px"}
                  />
                )}
                <UpdateLoadingCard
                  loadingId={ADMIN_USER_AVATAR_LOADING}
                  text={intl.formatMessage({ id: "app.navbar.updatingAvatar" })}
                  className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                />
              </div>
              {(isAdmin || isReseller) && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id={`admin-user-avatar-${id}`}
                    className="d-none"
                    accept={filesAllowed.join(",")}
                    onChange={(e) => handleSelectPhoto(e.target.files)}
                  />
                  <Label
                    for={`admin-user-avatar-${id}`}
                    className="btn btn-sm whiteButton border mt-2 mb-0 pointer"
                  >
                    <FontAwesomeIcon icon={["fas", "camera"]} className="mr-2" />
                    {intl.formatMessage({ id: "app.adminUsers.changeAvatar" })}
                  </Label>
                </>
              )}
              <h4 className="h4 my-2">
                {userRole?.admin 
                  ? intl.formatMessage({ id: "app.adminUsers.admin?" })
                  : intl.formatMessage({ id: "app.adminUsers.user?" })
                }
              </h4>
              <div className="d-flex align-items-center justify-content-center">
                <div className="m-2 d-flex pointer align-items-center">
                  <FontAwesomeIcon
                    icon={["fas", "info-circle"]}
                    className="mx-2"
                    size="1x"
                    onClick={goToPlans}
                  />
                  <div onClick={() => setEditPlan(true)}>
                    {(isAdmin || isReseller) && editPlan ? (
                      <InputWembii
                        mb={false}
                        type="select"
                        value={planId}
                        onChange={(value) => onAssignPlan(value)}
                      >
                        {oldPlan && userPlan && (
                          <option key={`current-${userPlan.id}`} value={"" + userPlan.id}>
                            {intl.formatMessage({ id: userPlan.name })}
                          </option>
                        )}
                        {plans.map(({ id: planOptionId, name }) => (
                          <option key={planOptionId} value={"" + planOptionId}>
                            {intl.formatMessage({ id: name })}
                          </option>
                        ))}
                        :
                      </InputWembii>
                    ) : (
                      plan && <PlanBadge show plan={plan} />
                    )}
                  </div>
                </div>
                <div className="m-2 d-flex pointer align-items-center">
                  <div onClick={() => setEditStatus(true)}>
                    {isAdmin && editStatus ? (
                      <InputWembii
                        mb={false}
                        type="select"
                        value={statusId}
                        onChange={(v) => onAssignStatus(v)}
                      >
                        {getAllUserStatus().map(({ id: statusOptionId, name }) => (
                          <option key={statusOptionId} value={statusOptionId}>
                            {name}
                          </option>
                        ))}
                        :
                      </InputWembii>
                    ) : (
                      <StatusBadge status={statusId} show />
                    )}
                  </div>
                </div>
                <div className="m-2 d-flex pointer align-items-center">
                  <ResellerSelector
                    reseller={reseller}
                    onAssignReseller={onAssignReseller}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-center">
                <div className="m-2 d-flex flex-column pointer justify-content-center">
                  <FreeTrialSelector
                    isFreeTrial={freeTrial}
                    freeTrialDays={freeTrialDays}
                    freeTrialDate={freeTrialDate}
                    onAssignFreeTrial={onAssignFreeTrial}
                  />
                </div>
              </div>
            </div>
            <Form className="px-2">
              <InputWembii
                label={intl.formatMessage({ id: "app.nameTxt" })}
                type="text"
                name="firstname"
                id="contact-firstname"
                placeholder={intl.formatMessage({ id: "app.nameTxt" })}
                value={firstName}
                disabled
              />
              <InputWembii
                label={intl.formatMessage({ id: "app.surnameTxt" })}
                type="text"
                name="lastname"
                id="contact-lastname"
                placeholder={intl.formatMessage({ id: "app.surnameTxt" })}
                value={lastName}
                disabled
              />
              <InputWembii
                label={intl.formatMessage({ id: "app.countryTxt" })}
                type="text"
                name="country"
                id="contact-country"
                placeholder={intl.formatMessage({ id: "app.countryTxt" })}
                value={country}
                disabled
              />
              <InputWembii
                label={intl.formatMessage({ id: "app.emailTxt" })}
                type="email"
                name="email"
                id="contact-email"
                placeholder={intl.formatMessage({ id: "app.emailTxt" })}
                value={email}
                disabled
              />
              <InputWembii
                label={intl.formatMessage({ id: "app.phoneTypeTxt" })}
                type="select"
                name="telephone-type"
                id="contact-telephone-type"
                placeholder={intl.formatMessage({ id: "app.phoneTypeTxt" })}
                value={phoneType}
                disabled
              >
                <option value=''></option>
                <option value='celphone'>
                  Celular
                </option>
                <option value='telephone'>
                  Teléfono fijo
                </option>
              </InputWembii>
              <InputWembii
                label={intl.formatMessage({ id: "app.phoneTxt" })}
                type="text"
                name="telephone"
                id="contact-telephone"
                placeholder={intl.formatMessage({ id: "app.phoneTxt" })}
                value={phoneNumber}
                disabled
              />
            </Form>
            {isAdmin && (
              <Button className="my-2" onClick={onClickCompanies}>
                <FontAwesomeIcon
                  icon={["fas", "building"]}
                  className="mx-2"
                  size="1x"
                />
                {intl.formatMessage({ id: "app.adminUsers.vinculatedCompanies" })}
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
